/**
 * Muting notifications per context (I-2915): a chat channel, a mailbox, a
 * calendar — whatever a notification type names as its context type. A mute
 * is a setting like every other: a field of the shell's account config key,
 *
 *   shell → notifications.mutes.<module key>.<context type>.<context id>
 *
 * holding `true` (muted until taken back), an RFC 3339 instant (muted until
 * then) or `false` (not muted — over a mute a tenant, company or design
 * gives). Master's notification service reads it when a notification is
 * produced; a type that is exempt (a mention, an approval request) notifies
 * all the same. An app mutes one of its own contexts through this helper and
 * never writes the document itself.
 *
 * **A mute lives on the ACCOUNT layer, never on a profile's.** A
 * notification is addressed to an account, not to a session: master reads
 * the recipient's preferences from the account layer and the layers before
 * it (platform, tenant, company, design). A session that works in another
 * profile than the default one writes that profile's layer by default —
 * which master would never see. So the helper reads what the account layer
 * amounts to (its own document over the layers before it) and writes it
 * explicitly (`?target=account:<own account id>`).
 *
 * The context id is what the app's notifications carry as `contextID` — the
 * app's backend decides its form (chat: the channel's plain id).
 */
import { computed, shallowRef, watch, type ComputedRef } from "vue";
import { fetchWithAuth, getApiOrigin } from "../auth/fetch";
import { useAccountConfig, type AccountConfigTarget } from "./accountConfig";
import { accountConfigLayerView } from "./layerView";
import {
  applyMergePatch,
  isJsonObject,
  jsonEqual,
  mergeJson,
  type JsonObject,
} from "./mergePatch";

/** The shell's account config key; `notifications` is one of its fields. */
export const NOTIFICATION_SETTINGS_KEY = "shell";

/** What a mute refers to. */
export interface NotificationMuteContext {
  /** The module key (`chat`). */
  module: string;
  /** The context type of the module's notification types (`channel`). */
  contextType: string;
  /** The context's id as the module's notifications carry it. */
  contextID: string;
}

/** A stored mute: without end, until an instant, or switched off. */
export type NotificationMuteValue = boolean | string;

/** The stored mute of a context in a `shell` document; undefined when none. */
export function notificationMuteValue(
  doc: unknown,
  context: NotificationMuteContext,
): NotificationMuteValue | undefined {
  let node: unknown = doc;
  for (const segment of [
    "notifications",
    "mutes",
    context.module,
    context.contextType,
    context.contextID,
  ]) {
    if (!isJsonObject(node)) return undefined;
    node = node[segment];
  }
  return typeof node === "boolean" || typeof node === "string"
    ? node
    : undefined;
}

/** Whether a stored mute holds at `now`: `true`, or an instant that lies ahead. */
export function notificationMuteActive(
  value: NotificationMuteValue | undefined,
  now: number = Date.now(),
): boolean {
  if (value === true) return true;
  if (typeof value !== "string") return false;
  const until = Date.parse(value);
  return Number.isFinite(until) && until > now;
}

/**
 * The merge patch of the `shell` key that sets a context's mute: `true`
 * (until taken back), a date (until then), `false` (not muted, over an
 * inherited mute) or `null` (removes the layer's own entry).
 */
export function notificationMutePatch(
  context: NotificationMuteContext,
  value: boolean | Date | null,
): JsonObject {
  const stored = value instanceof Date ? value.toISOString() : value;
  return {
    notifications: {
      mutes: {
        [context.module]: {
          [context.contextType]: { [context.contextID]: stored },
        },
      },
    },
  };
}

/**
 * What taking a mute back has to write. An own entry is removed (`null`) —
 * unless a layer before the account's mutes the context too: a removal
 * would leave that mute in force, so the account says `false`, which master
 * reads as „not muted" and which overrides the inherited `true`.
 */
export function notificationUnmuteValue(
  inherited: NotificationMuteValue | undefined,
  now: number = Date.now(),
): false | null {
  return notificationMuteActive(inherited, now) ? false : null;
}

/** The field of the shell key that holds the mutes: the unit a tenant or company locks. */
const NOTIFICATIONS_FIELD = "notifications";

/** A context's notification level (I-3826): everything, only mentions, nothing. */
export type NotificationLevel = "all" | "mentions" | "off";

/** The levels in the order a menu lists them. */
export const NOTIFICATION_LEVELS: readonly NotificationLevel[] = [
  "all",
  "mentions",
  "off",
];

/**
 * The merge patch of the `shell` key that sets a context's own level
 * (`shell` → `notifications.levels.<module>.<context type>.<context id>`);
 * `null` removes it, so the default level applies again.
 */
export function notificationLevelPatch(
  context: NotificationMuteContext,
  level: NotificationLevel | null,
): JsonObject {
  return {
    notifications: {
      levels: {
        [context.module]: {
          [context.contextType]: { [context.contextID]: level },
        },
      },
    },
  };
}

export interface NotificationMutes {
  /** Whether the context is muted now, as master sees the account (reactive). */
  isMuted(context: NotificationMuteContext): boolean;
  /** The same as a computed of a context that may change (the open channel); null: none. */
  muted(context: () => NotificationMuteContext | null): ComputedRef<boolean>;
  /** Mutes the context: until taken back, or until `until`. False when it failed. */
  mute(context: NotificationMuteContext, until?: Date): Promise<boolean>;
  /** Takes the mute back — also one a tenant, company or design gives. False when it failed. */
  unmute(context: NotificationMuteContext): Promise<boolean>;
  /**
   * Sets the context's own notification level on the account's layer;
   * `null` removes it (the default level applies). False when it failed.
   */
  setLevel(
    context: NotificationMuteContext,
    level: NotificationLevel | null,
  ): Promise<boolean>;
}

/**
 * The own account's id, for a session whose config answer does not name it
 * (it works in a non-default profile and the account has no row of the key
 * yet): the account's profiles carry it.
 */
async function fetchOwnAccountID(): Promise<number> {
  try {
    const response = await fetchWithAuth(
      `${getApiOrigin()}/v1/account/profiles`,
      { cache: "no-store" },
    );
    if (!response.ok) return 0;
    const body = (await response.json()) as { profiles?: unknown };
    const profiles = Array.isArray(body?.profiles) ? body.profiles : [];
    for (const profile of profiles) {
      const id = Number((profile as { accountID?: unknown })?.accountID);
      if (Number.isInteger(id) && id > 0) return id;
    }
  } catch {
    // Unknown, then: nothing is written.
  }
  return 0;
}

/**
 * The account's mutes, read and written through the account config path
 * (`useAccountConfig`): one shared store of the `shell` key per tab, its
 * layers read along, writes as merge patches onto the account's layer.
 */
export function useNotificationMutes(): NotificationMutes {
  const config = useAccountConfig<JsonObject>(NOTIFICATION_SETTINGS_KEY, {
    layers: true,
  });

  /**
   * The account's layer: the session's own write target while it works in
   * the default profile, else the account row among the layers; null while
   * neither names it.
   */
  const accountTarget = computed<AccountConfigTarget | null>(() => {
    const target = config.target.value;
    if (target?.subjectType === "account" && target.subjectId > 0) {
      return { subjectType: "account", subjectId: target.subjectId };
    }
    const layer = (config.layers.value ?? []).find(
      (item) => item.subjectType === "account" && item.subjectId > 0,
    );
    return layer ? { subjectType: "account", subjectId: layer.subjectId } : null;
  });

  // This tab's own writes until master's layers show them: the account layer
  // is no default target of a profile session, so the store does not paint
  // them ahead itself.
  const written = shallowRef<JsonObject[]>([]);
  /** The writes master has answered: they go with the next layers it sends. */
  const settled = new WeakSet<JsonObject>();
  watch(
    () => config.layers.value,
    () => {
      written.value = written.value.filter((patch) => !settled.has(patch));
    },
  );

  /**
   * What master's layers say of the account: its own document (none of a
   * field a tenant or company locked — master drops the account's value
   * there) and what the layers before it give. Without layers (not read yet)
   * the session's document stands in for the latter.
   */
  const stored = computed<{ own: JsonObject; inherited: JsonObject }>(() => {
    const layers = config.layers.value;
    if (!layers) return { own: {}, inherited: config.effective.value ?? {} };
    const view = accountConfigLayerView(
      layers,
      accountTarget.value ?? { subjectType: "account", subjectId: 0 },
    );
    return {
      own: NOTIFICATIONS_FIELD in view.lockedBy ? {} : view.own,
      inherited: view.inherited,
    };
  });

  /** The same with this tab's writes that master's layers do not show yet. */
  const account = computed<{ own: JsonObject; inherited: JsonObject }>(() => {
    let own: unknown = stored.value.own;
    for (const patch of written.value) own = applyMergePatch(own, patch);
    return {
      own: isJsonObject(own) ? own : {},
      inherited: stored.value.inherited,
    };
  });

  function isMuted(context: NotificationMuteContext): boolean {
    const { own, inherited } = account.value;
    return notificationMuteActive(
      notificationMuteValue(mergeJson(inherited, own), context),
    );
  }

  async function write(
    context: NotificationMuteContext,
    value: boolean | Date | null,
  ): Promise<boolean> {
    return writePatch(context, notificationMutePatch(context, value));
  }

  /** Writes a patch of the context's settings onto the account's layer. */
  async function writePatch(
    context: NotificationMuteContext,
    patch: JsonObject,
  ): Promise<boolean> {
    if (!context.module || !context.contextType || !context.contextID) {
      return false;
    }
    const id = accountTarget.value?.subjectId || (await fetchOwnAccountID());
    if (!id) return false;
    written.value = [...written.value, patch];
    const result = await config.patch(patch, {
      target: { subjectType: "account", subjectId: id },
      immediate: true,
    });
    const own = stored.value.own;
    if (result === null || jsonEqual(applyMergePatch(own, patch), own)) {
      // Refused or lost: back to what master holds. Or its layers show the
      // write already (the account layer is the session's own target).
      written.value = written.value.filter((item) => item !== patch);
      return result !== null;
    }
    // The store reads the layers again; the write goes with their arrival.
    settled.add(patch);
    return true;
  }

  return {
    isMuted,
    muted: (context) =>
      computed(() => {
        const current = context();
        return current ? isMuted(current) : false;
      }),
    mute: (context, until) => write(context, until ?? true),
    unmute: (context) =>
      write(
        context,
        notificationUnmuteValue(
          notificationMuteValue(account.value.inherited, context),
        ),
      ),
    setLevel: (context, level) =>
      writePatch(context, notificationLevelPatch(context, level)),
  };
}
